import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  stockSignalsService,
  StockSignal,
  MacroState,
} from '../services/stockSignalsService';
import { IntradayChart } from './IntradayChart';
import { MacroStressPanel } from './MacroStressPanel';
import { ShouldITradeCard } from './ShouldITradeCard';
import { marketRegimeService, MarketRegime } from '../services/marketRegimeService';
import { Navigation } from './Navigation';
import './Dashboard.css';
import { RiskDisclaimer } from './shared/RiskDisclaimer';

class StockSignalsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="dashboard-container">
          <Navigation />
          <div className="dashboard-content" style={{ padding: '2rem' }}>
            <h2 style={{ color: '#f87171' }}>Something went wrong</h2>
            <pre style={{
              backgroundColor: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '8px',
              padding: '1rem',
              color: '#fca5a5',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '0.85em'
            }}>
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <button
              onClick={() => this.setState({ error: null })}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

type TabType = 'positions' | 'newSignals' | 'history';
type HistoryPreset = '4weeks' | '8weeks' | '12weeks';

function getWeeksAgoDate(weeks: number): string {
  const d = new Date();
  d.setDate(d.getDate() - weeks * 7);
  return d.toISOString().split('T')[0];
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeEST(timestamp: string): string {
  if (!timestamp) return '';
  try {
    let dateStr = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
      dateStr = timestamp + 'Z';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' ET';
  } catch {
    return '';
  }
}

function formatDateTimeEST(timestamp: string): string {
  if (!timestamp) return 'N/A';
  try {
    let dateStr = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
      dateStr = timestamp + 'Z';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return formatDateShort(timestamp);
    const datePart = d.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', year: 'numeric'
    });
    const timePart = d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `${datePart} at ${timePart} ET`;
  } catch {
    return formatDateShort(timestamp);
  }
}

function daysSince(dateStr: string): number {
  if (!dateStr) return 0;
  const opened = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - opened.getTime()) / (1000 * 60 * 60 * 24));
}

function pnlColor(pnl: number): string {
  return pnl >= 0 ? '#28a745' : '#dc3545';
}

function pnlSign(pnl: number): string {
  return pnl >= 0 ? '+' : '';
}

function trendArrow(sma20: number | null, sma50: number | null): string {
  if (!sma20 || !sma50) return '--';
  if (sma20 > sma50) return '20>50 \u2191';
  if (sma20 < sma50) return '20<50 \u2193';
  return '20\u224850';
}

function bbLabel(pos: number | null): string {
  if (pos === null) return '--';
  if (pos >= 0.85) return 'Upper';
  if (pos >= 0.6) return 'Mid-Upper';
  if (pos >= 0.4) return 'Middle';
  if (pos >= 0.15) return 'Mid-Lower';
  return 'Lower';
}

function progressPct(entry: number, current: number, target: number): number {
  if (target <= entry) return 0;
  return Math.max(0, Math.min(100, ((current - entry) / (target - entry)) * 100));
}

function scoreColor(score: number): string {
  if (score >= 80) return '#28a745';
  if (score >= 70) return '#ffc107';
  return '#64748b';
}

// Feature flag — set to true once historical win rates are strong enough to
// surface to users. Hidden 2026-04-26 because current sample shows weak rates.
const SHOW_STATS_BANNER = false;

// ── Win-rate / P&L stats over the currently visible signals ──────────────────

interface SignalStats {
  total: number;
  closed: number;
  open: number;
  wins: number;
  losses: number;
  winRatePct: number | null;          // null if no closed signals
  avgRealizedPct: number | null;      // avg % return per closed trade
  cumulativeRealizedPct: number;      // sum of % returns (if equal-size positions)
  avgUnrealizedPct: number | null;    // avg % unrealized for open trades
  cumulativeUnrealizedPct: number;
}

function computeSignalStats(signals: StockSignal[]): SignalStats {
  const closed = signals.filter(s => s.status === 'closed' && typeof s.realized_pnl_pct === 'number');
  const open = signals.filter(s => s.status === 'open');
  const wins = closed.filter(s => (s.realized_pnl_pct ?? 0) > 0).length;
  const cumulativeRealizedPct = closed.reduce((sum, s) => sum + (s.realized_pnl_pct ?? 0), 0);
  const cumulativeUnrealizedPct = open.reduce((sum, s) => sum + (s.unrealized_pnl_pct ?? 0), 0);
  return {
    total: signals.length,
    closed: closed.length,
    open: open.length,
    wins,
    losses: closed.length - wins,
    winRatePct: closed.length > 0 ? (wins / closed.length) * 100 : null,
    avgRealizedPct: closed.length > 0 ? cumulativeRealizedPct / closed.length : null,
    cumulativeRealizedPct,
    avgUnrealizedPct: open.length > 0 ? cumulativeUnrealizedPct / open.length : null,
    cumulativeUnrealizedPct,
  };
}

const StatsCard: React.FC<{ label: string; value: string; sub?: string; valueColor?: string }> = ({ label, value, sub, valueColor }) => (
  <div style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '0.75rem 1rem',
    minWidth: 140,
    flex: '1 1 auto',
  }}>
    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color: valueColor || '#f1f5f9' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
  </div>
);

/**
 * SignalStatsBanner — renders a compact stats strip for whichever signals
 * are currently visible. `mode='closed'` shows win/loss metrics (for the
 * "This Week" and "History" tabs); `mode='open'` shows unrealized metrics.
 */
const SignalStatsBanner: React.FC<{ signals: StockSignal[]; mode: 'closed' | 'open' }> = ({ signals, mode }) => {
  const stats = useMemo(() => computeSignalStats(signals), [signals]);
  if (signals.length === 0) return null;

  const fmtPct = (n: number | null, sign = false) =>
    n == null ? '—' : `${sign && n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
  const greenRed = (n: number | null) =>
    n == null ? '#f1f5f9' : n > 0 ? '#22c55e' : n < 0 ? '#ef4444' : '#f1f5f9';

  if (mode === 'open') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: '1rem' }}>
        <StatsCard label="Open Positions" value={String(stats.open)} />
        <StatsCard
          label="Avg Unrealized"
          value={fmtPct(stats.avgUnrealizedPct, true)}
          valueColor={greenRed(stats.avgUnrealizedPct)}
        />
        <StatsCard
          label="Cumulative Unrealized"
          value={fmtPct(stats.cumulativeUnrealizedPct, true)}
          sub="If equal-size positions"
          valueColor={greenRed(stats.cumulativeUnrealizedPct)}
        />
      </div>
    );
  }

  // mode === 'closed'
  if (stats.closed === 0) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: '1rem' }}>
        <StatsCard label="Closed Picks" value="0" sub="No completed trades in this view" />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: '1rem' }}>
      <StatsCard
        label="Win Rate"
        value={fmtPct(stats.winRatePct)}
        sub={`${stats.wins}W · ${stats.losses}L (${stats.closed} closed)`}
        valueColor={stats.winRatePct != null && stats.winRatePct >= 50 ? '#22c55e' : '#f59e0b'}
      />
      <StatsCard
        label="Avg Return / Trade"
        value={fmtPct(stats.avgRealizedPct, true)}
        valueColor={greenRed(stats.avgRealizedPct)}
      />
      <StatsCard
        label="Cumulative Return"
        value={fmtPct(stats.cumulativeRealizedPct, true)}
        sub="If equal-size positions"
        valueColor={greenRed(stats.cumulativeRealizedPct)}
      />
    </div>
  );
};

const StockSignalsInner: React.FC = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('positions');
  const [openSignals, setOpenSignals] = useState<StockSignal[]>([]);
  const [closedSignals, setClosedSignals] = useState<StockSignal[]>([]);
  const [macro, setMacro] = useState<MacroState | null>(null);
  const [marketRegime, setMarketRegime] = useState<MarketRegime | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // History state
  const [historyPreset, setHistoryPreset] = useState<HistoryPreset>('4weeks');
  const [historicalByWeek, setHistoricalByWeek] = useState<{ [weekKey: string]: StockSignal[] }>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<string>>(new Set());

  // Expanded card tracking
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  useEffect(() => { loadSignals(); }, []);

  const loadSignals = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const data = await stockSignalsService.getSignals(token);
      setOpenSignals(data.openSignals || []);
      setClosedSignals(data.closedSignals || []);
      // Macro for /stock-signals comes EXCLUSIVELY from /market/regime's
      // equity_macro field (VIX + SPY, direction-aware) since 2026-06-27.
      // Previously this page merged a getStockSignals-bundled macro with the
      // /market/regime macro -- both 4-factor -- producing an orange-to-red
      // flash and the wrong (cross-asset) reading for an equity product.
      // Single source = no flash. Bond/commodity stress is not relevant for
      // individual stock trading; equity_macro keeps the lens on VIX + SPY.
      marketRegimeService.get(token).then((r) => {
        setMarketRegime(r);
        setMacro((r.equity_macro as any) || null);
      }).catch(() => {});
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to load stock signals';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (preset: HistoryPreset) => {
    if (!token) return;
    const weeks = preset === '4weeks' ? 4 : preset === '8weeks' ? 8 : 12;
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const data = await stockSignalsService.getHistoricalSignals(token, {
        startDate: getWeeksAgoDate(weeks),
        endDate: getTodayDate(),
      });
      setHistoricalByWeek(data.signalsByWeek || {});
      const sortedWeeks = Object.keys(data.signalsByWeek || {}).sort().reverse();
      setCollapsedWeeks(new Set(sortedWeeks.slice(2)));
    } catch (err: any) {
      setHistoryError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory(historyPreset);
    }
  }, [activeTab, historyPreset]);

  const toggleCard = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleWeek = (key: string) => {
    setCollapsedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Premium gate
  const isPremium = user?.effectiveTier &&
    ['premium', 'professional', 'advanced', 'pro', 'premier'].includes(user.effectiveTier.toLowerCase());

  // Free user preview signal
  const [previewSignal, setPreviewSignal] = useState<any>(null);
  useEffect(() => {
    if (!isPremium && token) {
      stockSignalsService.getPreview(token, 'stock').then(data => {
        if (data.previews?.stock) setPreviewSignal(data.previews.stock);
      }).catch(() => {});
    }
  }, [isPremium, token]);

  return (
    <div className="dashboard-container">
      <Navigation />

      <div className="dashboard-content">
        {!isPremium ? (
          <div style={{ padding: '1.5rem' }}>
            <div className="welcome-section">
              <h2>Stock Swing Research</h2>
              <p>Multi-factor analysis scanning 100+ stocks every hour — Long and Short picks with entry, stop, and target prices.</p>
            </div>

            {/* Free preview signal */}
            {previewSignal && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: 13, color: '#4ade80', fontWeight: 600, marginBottom: 8 }}>
                  TODAY'S FREE PICK
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                  border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: 10, padding: '20px 24px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.5em', color: '#f9fafb' }}>
                        {previewSignal.symbol}
                        <span style={{
                          padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.4em', fontWeight: 'bold',
                          verticalAlign: 'middle', marginLeft: 8,
                          backgroundColor: previewSignal.direction === 'short' ? '#dc2626' : '#16a34a', color: 'white',
                        }}>
                          {previewSignal.direction === 'short' ? '▼ SELL SHORT' : '▲ BUY LONG'}
                        </span>
                      </h3>
                      <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.9em' }}>
                        {previewSignal.strategy_name} &bull; Score: {previewSignal.score}/100
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.8em', fontWeight: 700, color: '#f9fafb' }}>
                        ${previewSignal.current_price || previewSignal.entry_price}
                      </div>
                      {previewSignal.unrealized_pnl_pct != null && previewSignal.unrealized_pnl_pct !== 0 && (
                        <div style={{ color: previewSignal.unrealized_pnl_pct >= 0 ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                          {previewSignal.unrealized_pnl_pct >= 0 ? '+' : ''}{previewSignal.unrealized_pnl_pct.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 20, marginTop: 12, color: '#94a3b8', fontSize: 13 }}>
                    <span>Entry: <strong style={{ color: '#d1d5db' }}>${previewSignal.entry_price}</strong></span>
                    <span>Stop: <strong style={{ color: '#f87171' }}>${previewSignal.stop_price}</strong></span>
                    <span>Target: <strong style={{ color: '#4ade80' }}>${previewSignal.target_price}</strong></span>
                  </div>
                </div>
              </div>
            )}

            {/* Blurred placeholder signals */}
            <div style={{ position: 'relative' }}>
              <div style={{ filter: 'blur(8px)', pointerEvents: 'none', opacity: 0.5 }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '20px 24px', marginBottom: 12,
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <h3 style={{ margin: 0, color: '#f9fafb' }}>{'AAPL MSFT NVDA'.split(' ')[i - 1]} <span style={{ fontSize: '0.5em', backgroundColor: '#16a34a', color: 'white', padding: '2px 6px', borderRadius: 4 }}>▲ BUY LONG</span></h3>
                        <p style={{ color: '#94a3b8', margin: '4px 0 0', fontSize: 13 }}>Momentum &bull; Score: {75 + i * 3}/100</p>
                      </div>
                      <div style={{ fontSize: '1.5em', fontWeight: 700, color: '#f9fafb' }}>${(180 + i * 30).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
              }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f9fafb' }}>
                  {openSignals.length > 0 ? `${openSignals.length} more picks available` : 'Unlock all picks'}
                </div>
                <p style={{ color: '#94a3b8', fontSize: 13, margin: 0, textAlign: 'center' }}>
                  Get real-time stock research with entry, stop, and target prices — updated every hour
                </p>
                <Link
                  to="/upgrade"
                  style={{
                    display: 'inline-block', backgroundColor: '#3b82f6', color: 'white',
                    padding: '0.75rem 2rem', textDecoration: 'none', borderRadius: 8,
                    fontWeight: 'bold', fontSize: 15,
                  }}
                >
                  Upgrade to Premier — $299.90/yr
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="welcome-section">
              <h2>Stock Swing Research <span className="beta-badge" style={{ fontSize: 10, marginLeft: 6 }}>BETA</span></h2>
              <p>Swing trades generated from multi-factor analysis (Technicals, Sentiment, Volume, IVR) &mdash; Long and Short picks based on market direction</p>

              {/* Should I trade right now? — macro + market regime summary */}
              {macro && <ShouldITradeCard macro={macro} regime={marketRegime} tradeNoun="new positions" />}
              {/* Macro stress banner — above the tabs */}
              {macro && <MacroStressPanel macro={macro} penaltyTarget="long" />}

              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                gap: '0',
                marginTop: '1rem',
                borderBottom: '2px solid rgba(255,255,255,0.1)'
              }}>
                {([
                  ['positions', `Open Positions (${openSignals.length})`],
                  ['newSignals', `This Week (${closedSignals.length})`],
                  ['history', 'History'],
                ] as [TabType, string][]).map(([tab, label]) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: activeTab === tab ? '#3b82f6' : 'transparent',
                      color: activeTab === tab ? 'white' : '#94a3b8',
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      fontWeight: activeTab === tab ? 'bold' : 'normal',
                      fontSize: '1em'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Controls for positions/this-week tabs */}
              {(activeTab === 'positions' || activeTab === 'newSignals') && (
                <div style={{ marginTop: '1rem' }}>
                  <button
                    onClick={loadSignals}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: loading ? '#475569' : '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {loading ? 'Refreshing...' : '\u21BB Refresh Research'}
                  </button>
                </div>
              )}

              {/* History controls */}
              {activeTab === 'history' && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-end'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85em', color: '#94a3b8', marginBottom: '0.25rem' }}>
                        Time Range
                      </label>
                      <select
                        value={historyPreset}
                        onChange={(e) => setHistoryPreset(e.target.value as HistoryPreset)}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          border: '1px solid rgba(255,255,255,0.2)',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#ffffff',
                          fontSize: '1em',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="4weeks">Last 4 weeks</option>
                        <option value="8weeks">Last 8 weeks</option>
                        <option value="12weeks">Last 12 weeks</option>
                      </select>
                    </div>
                    <button
                      onClick={() => loadHistory(historyPreset)}
                      disabled={historyLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: historyLoading ? '#475569' : '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: historyLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {historyLoading ? 'Loading...' : '\u21BB Refresh'}
                    </button>
                  </div>
                </div>
              )}

              {/* Stats banner hidden 2026-04-26 — win-rate too low to show users yet.
                  Component (SignalStatsBanner / computeSignalStats) is intact below;
                  flip SHOW_STATS_BANNER to true to bring it back per tab. */}
              {SHOW_STATS_BANNER && activeTab === 'positions' && (
                <SignalStatsBanner signals={openSignals} mode="open" />
              )}
              {SHOW_STATS_BANNER && activeTab === 'newSignals' && (
                <SignalStatsBanner signals={closedSignals} mode="closed" />
              )}
              {SHOW_STATS_BANNER && activeTab === 'history' && !historyLoading && (
                <SignalStatsBanner
                  signals={Object.values(historicalByWeek).flat()}
                  mode="closed"
                />
              )}
            </div>

            {/* Error display */}
            {activeTab !== 'history' && error && (
              <div style={{
                backgroundColor: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '2rem',
                color: '#f87171'
              }}>
                <strong>Error:</strong> {error}
              </div>
            )}
            {activeTab === 'history' && historyError && (
              <div style={{
                backgroundColor: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '2rem',
                color: '#f87171'
              }}>
                <strong>Error:</strong> {historyError}
              </div>
            )}

            {/* Open Positions Tab */}
            {activeTab === 'positions' && (
              loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <p>Loading research...</p>
                </div>
              ) : openSignals.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <h3>No Active Opportunities</h3>
                  <p>New picks are generated hourly during market hours (9 AM \u2013 5 PM ET).</p>
                </div>
              ) : (
                <div style={{ marginTop: '2rem', display: 'grid', gap: '1rem' }}>
                  {openSignals.map(sig => (
                    <OpenPositionCard
                      key={sig.signal_id}
                      signal={sig}
                      isExpanded={expandedCards.has(sig.signal_id)}
                      onToggle={() => toggleCard(sig.signal_id)}
                    />
                  ))}
                </div>
              )
            )}

            {/* This Week Tab */}
            {activeTab === 'newSignals' && (
              loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <p>Loading research...</p>
                </div>
              ) : closedSignals.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <h3>No Closed Trades This Week</h3>
                  <p>No completed trades this week. Closed positions and their results will appear here.</p>
                </div>
              ) : (
                <div style={{ marginTop: '2rem', display: 'grid', gap: '1rem' }}>
                  {closedSignals.map(sig => (
                    <ClosedSignalCard key={sig.signal_id} signal={sig} />
                  ))}
                </div>
              )
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              historyLoading && Object.keys(historicalByWeek).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <p>Loading historical research...</p>
                </div>
              ) : Object.keys(historicalByWeek).length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}>
                  <h3>No Opportunities Found</h3>
                  <p>No trade opportunities were identified for the selected date range. The system only surfaces trades when market conditions meet our quality criteria.</p>
                </div>
              ) : (
                <div style={{ marginTop: '2rem' }}>
                  {Object.keys(historicalByWeek).sort().reverse().map(weekKey => {
                    const [startStr] = weekKey.split(':');
                    const signals = historicalByWeek[weekKey];
                    const isCollapsed = collapsedWeeks.has(weekKey);
                    const weekPnl = signals.reduce((sum, s) => sum + (s.realized_pnl_pct || 0), 0) / signals.length;

                    return (
                      <div key={weekKey} style={{ marginBottom: '1.5rem' }}>
                        <div
                          onClick={() => toggleWeek(weekKey)}
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            padding: '0.75rem 1rem',
                            borderRadius: isCollapsed ? '8px' : '8px 8px 0 0',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <h3 style={{ margin: 0, color: 'white', fontSize: '1.1em' }}>
                            Week of {formatDate(startStr)}
                            <span style={{ fontSize: '0.85em', marginLeft: '0.75rem', color: '#64748b' }}>
                              ({signals.length} pick{signals.length !== 1 ? 's' : ''})
                            </span>
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ color: pnlColor(weekPnl), fontWeight: 'bold', fontSize: '1em' }}>
                              Avg {pnlSign(weekPnl)}{weekPnl.toFixed(1)}%
                            </span>
                            <span style={{ color: 'white', fontSize: '1.2em' }}>
                              {isCollapsed ? '\u25B8' : '\u25BE'}
                            </span>
                          </div>
                        </div>

                        {!isCollapsed && (
                          <div style={{
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderTop: 'none',
                            borderRadius: '0 0 8px 8px',
                            padding: '1rem',
                            display: 'grid',
                            gap: '0.75rem'
                          }}>
                            {signals.map(sig => (
                              <ClosedSignalCard key={sig.signal_id} signal={sig} compact />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* Risk Disclaimer */}
            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              backgroundColor: 'rgba(251,191,36,0.15)',
              border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: '8px',
              fontSize: '0.85em'
            }}>
              <strong>Risk Disclaimer:</strong> Stock swing research is for informational purposes only. Past performance does not guarantee future results. Always conduct your own research and consult with a financial advisor before making investment decisions.
            </div>
          </>
        )}
      </div>
      <RiskDisclaimer />
    </div>
  );
};

export const StockSignals: React.FC = () => (
  <StockSignalsErrorBoundary>
    <StockSignalsInner />
  </StockSignalsErrorBoundary>
);

/* --- Open Position Card ------------------------------------------------- */

const OpenPositionCard: React.FC<{
  signal: StockSignal;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ signal: rawSig, isExpanded, onToggle }) => {
  // Coerce key numeric fields (DynamoDB may return strings)
  const sig = {
    ...rawSig,
    entry_price: Number(rawSig.entry_price) || 0,
    current_price: Number(rawSig.current_price) || 0,
    target_price: Number(rawSig.target_price) || 0,
    trailing_stop: Number(rawSig.trailing_stop) || 0,
    stop_loss: Number(rawSig.stop_loss) || 0,
    high_since_entry: Number(rawSig.high_since_entry) || 0,
    unrealized_pnl_pct: Number(rawSig.unrealized_pnl_pct) || 0,
    risk_reward_ratio: Number(rawSig.risk_reward_ratio) || 0,
    score: Number(rawSig.score) || 0,
  };
  const pnl = sig.unrealized_pnl_pct || 0;
  const days = sig.hold_days || daysSince(sig.opened_at);
  const progress = progressPct(sig.entry_price, sig.current_price, sig.target_price);
  const tech = sig.technicals || {};

  const atr = Number(tech.atr_14) || 0;
  const nextTrigger = sig.high_since_entry + 0.01;
  const nextStop = atr > 0 ? Math.max(sig.trailing_stop, nextTrigger - atr * 1.5) : sig.trailing_stop;
  const nextStopPnl = sig.entry_price > 0 ? ((nextStop - sig.entry_price) / sig.entry_price * 100) : 0;

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Header row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'start',
          padding: '1.5rem', cursor: 'pointer'
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#60a5fa', fontSize: '1.5em', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {sig.symbol}
            <span style={{
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.4em',
              fontWeight: 'bold',
              verticalAlign: 'middle',
              backgroundColor: (sig as any).direction === 'short' ? '#dc2626' : '#16a34a',
              color: 'white',
            }}>
              {(sig as any).direction === 'short' ? '▼ SELL SHORT' : '▲ BUY LONG'}
            </span>
          </h3>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {sig.strategy_name}
            <span style={{ color: '#64748b' }}>
              &bull; OPEN since {formatDateTimeEST(sig.opened_at)} ({days} day{days !== 1 ? 's' : ''})
            </span>
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '1.8em',
            fontWeight: 'bold',
            color: scoreColor(sig.score)
          }}>
            {sig.score}
          </div>
          <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>Score</div>
        </div>
      </div>

      {/* P&L and progress section */}
      <div style={{
        padding: '1rem 1.5rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(255,255,255,0.03)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div>
            <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>Entry</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>${sig.entry_price.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>Current</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>${sig.current_price.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>P&L</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: pnlColor(pnl) }}>
              {pnlSign(pnl)}{pnl.toFixed(2)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>Target</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              ${sig.target_price.toFixed(2)}
              <span style={{ fontSize: '0.7em', color: '#94a3b8', marginLeft: '0.25rem' }}>
                (+{((sig.target_price - sig.entry_price) / sig.entry_price * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${Math.min(100, progress)}%`,
              background: progress >= 100 ? '#28a745' : '#3b82f6',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: '0.85em', color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {progress.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Intraday Chart */}
      {sig.intraday_candles && sig.intraday_candles.length > 0 && (
        <IntradayChart
          candles={sig.intraday_candles}
          entryPrice={sig.entry_price}
          targetPrice={sig.target_price}
          stopLoss={sig.trailing_stop}
          symbol={sig.symbol}
        />
      )}

      {/* Stop Loss section */}
      <div style={{
        padding: '1rem 1.5rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(255,255,255,0.03)'
      }}>
        <div style={{
          padding: '0.75rem',
          backgroundColor: 'rgba(239,68,68,0.1)',
          borderRadius: '4px',
          fontSize: '0.9em'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <strong style={{ color: '#f87171' }}>Stop Loss: </strong>
              <span style={{ color: '#f87171', fontWeight: 'bold', fontSize: '1.1em' }}>${sig.trailing_stop.toFixed(2)}</span>
              <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>
                ({((sig.trailing_stop - sig.entry_price) / sig.entry_price * 100).toFixed(1)}% from entry)
              </span>
            </div>
            <div style={{ fontSize: '0.85em', color: '#64748b' }}>
              Based on ATR(14){atr > 0 ? ` $${atr.toFixed(2)}` : ''} x 1.5
            </div>
          </div>
          {sig.trailing_stop !== sig.stop_loss && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.9em', color: '#cbd5e1' }}>
              Original: <span style={{ color: '#64748b' }}>${sig.stop_loss.toFixed(2)}</span>
              {' \u2192 '}Trailing: <span style={{ color: '#ffc107', fontWeight: 'bold' }}>${sig.trailing_stop.toFixed(2)}</span>
              {sig.stop_adjustments?.length > 0 && (
                <span style={{ color: '#64748b' }}>
                  {' '}(adjusted {formatDateShort(sig.stop_adjustments[sig.stop_adjustments.length - 1]?.date)})
                </span>
              )}
            </div>
          )}
          {atr > 0 && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.85em', color: '#94a3b8' }}>
              Trailing: if price exceeds ${sig.high_since_entry.toFixed(2)}, stop moves to ${nextStop.toFixed(2)}
              {nextStopPnl > 0 && <span style={{ color: '#28a745' }}> (lock in +{nextStopPnl.toFixed(1)}%)</span>}
            </div>
          )}
        </div>
      </div>

      {/* Technicals strip */}
      <div style={{
        padding: '0.75rem 1.5rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', gap: '1.5rem', flexWrap: 'wrap',
        fontSize: '0.85em', color: '#cbd5e1'
      }}>
        <span>RSI <strong>{tech.rsi_14?.toFixed(0) ?? '--'}</strong></span>
        <span>MACD <strong>{tech.macd_histogram != null ? (tech.macd_histogram >= 0 ? '+' : '') + tech.macd_histogram.toFixed(2) : '--'}</strong></span>
        <span>SMA <strong>{trendArrow(tech.sma_20, tech.sma_50)}</strong></span>
        <span>BB <strong>{bbLabel(tech.bb_position)}</strong></span>
        <span>Vol <strong>{tech.volume_ratio?.toFixed(1) ?? '--'}x</strong></span>
        <span>AVWAP <strong style={{ color: (tech.avwap_swing_distance ?? 0) >= 0 ? '#4ade80' : '#f87171' }}>
          {tech.avwap_swing_distance != null ? (tech.avwap_swing_distance >= 0 ? '+' : '') + tech.avwap_swing_distance.toFixed(1) + '%' : '--'}
        </strong></span>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(255,255,255,0.03)'
        }}>
          {/* Score breakdown */}
          {sig.score_breakdown && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: 'rgba(59,130,246,0.1)',
              borderRadius: '4px',
              fontSize: '0.85em'
            }}>
              <strong>Score Breakdown:</strong>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '0.5rem',
                marginTop: '0.5rem'
              }}>
                <div>Technical: {sig.score_breakdown.technical}/30</div>
                <div>Volume: {sig.score_breakdown.volume}/15</div>
                <div>IVR: {sig.score_breakdown.ivr}/10</div>
                <div>Trend: {sig.score_breakdown.trend}/10</div>
                <div>Market: {sig.score_breakdown.market}/10</div>
                {sig.score_breakdown.intraday_timing != null && sig.score_breakdown.intraday_timing > 0 && (
                  <div style={{ color: '#22c55e' }}>Intraday Timing: +{sig.score_breakdown.intraday_timing}/5</div>
                )}
              </div>
            </div>
          )}

          {/* Rationale */}
          {sig.rationale && sig.rationale.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Rationale:</strong>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {sig.rationale.map((r, i) => (
                  <li key={i} style={{ marginBottom: '0.25rem', fontSize: '0.9em', color: '#94a3b8' }}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Stop adjustment history */}
          {sig.stop_adjustments && sig.stop_adjustments.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Stop Adjustments:</strong>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {sig.stop_adjustments.map((adj, i) => (
                  <li key={i} style={{ marginBottom: '0.25rem', fontSize: '0.9em', color: '#94a3b8' }}>
                    {formatDateShort(adj.date)}: ${Number(adj.old_stop).toFixed(2)} {'\u2192'} ${Number(adj.new_stop).toFixed(2)}
                    (triggered at ${Number(adj.trigger_price).toFixed(2)})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risk/Reward and meta */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            padding: '0.75rem',
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: '4px',
            fontSize: '0.85em'
          }}>
            <div>
              <div style={{ color: '#94a3b8' }}>Risk:Reward</div>
              <div style={{ fontWeight: 'bold' }}>{sig.risk_reward_ratio?.toFixed(2)}</div>
            </div>
            <div>
              <div style={{ color: '#94a3b8' }}>Expected Hold</div>
              <div style={{ fontWeight: 'bold' }}>{sig.expected_hold_days} days</div>
            </div>
            <div>
              <div style={{ color: '#94a3b8' }}>High Since Entry</div>
              <div style={{ fontWeight: 'bold' }}>${sig.high_since_entry.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* --- Closed Signal Card ------------------------------------------------- */

const ClosedSignalCard: React.FC<{ signal: StockSignal; compact?: boolean }> = ({ signal: rawSig, compact }) => {
  const sig = {
    ...rawSig,
    entry_price: Number(rawSig.entry_price) || 0,
    close_price: Number(rawSig.close_price) || 0,
    realized_pnl_pct: Number(rawSig.realized_pnl_pct) || 0,
  };
  const pnl = sig.realized_pnl_pct || 0;

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px',
      padding: compact ? '1rem' : '1.5rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span style={{ fontWeight: 'bold', color: '#60a5fa', fontSize: compact ? '1.1em' : '1.5em' }}>
              {sig.symbol}
            </span>
            <span style={{
              padding: '0.15rem 0.4rem',
              borderRadius: '3px',
              fontSize: '0.75em',
              fontWeight: 'bold',
              backgroundColor: '#7c3aed',
              color: 'white'
            }}>
              CLOSED
            </span>
          </div>
          <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>
            {sig.strategy_name}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: compact ? '1.5em' : '1.8em',
            fontWeight: 'bold',
            color: pnlColor(pnl)
          }}>
            {pnlSign(pnl)}{pnl.toFixed(2)}%
          </div>
          <div style={{ fontSize: '0.75em', color: '#94a3b8' }}>
            Score: {sig.score}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '0.75rem',
        marginBottom: sig.close_reason ? '0.75rem' : 0,
        fontSize: '0.85em'
      }}>
        <div>
          <div style={{ color: '#94a3b8' }}>Entry</div>
          <div style={{ fontWeight: 'bold' }}>${sig.entry_price.toFixed(2)}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8' }}>Close</div>
          <div style={{ fontWeight: 'bold' }}>${(sig.close_price || 0).toFixed(2)}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8' }}>Held</div>
          <div style={{ fontWeight: 'bold' }}>{sig.hold_days || daysSince(sig.opened_at)} days</div>
        </div>
      </div>

      {sig.close_reason && (
        <div style={{
          padding: '0.5rem 0.75rem',
          borderRadius: '4px',
          backgroundColor: 'rgba(251,191,36,0.15)',
          fontSize: '0.85em',
          color: '#fbbf24',
          borderLeft: '3px solid #fbbf24'
        }}>
          {sig.close_reason}
        </div>
      )}
    </div>
  );
};
